import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@ui/shadCN/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-base font-medium transition-all hover:cursor-pointer disabled:cursor-default disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 focus-visible:underline aria-invalid:ring-destructive/20 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-accent",
        destructive: "bg-destructive text-white hover:bg-destructive/90 ",
        outline_destructive:
          "text-destructive border border-destructive bg-background hover:bg-accent hover:text-accent-foreground hover:border-accent",
        outline:
          "text-primary border border-primary bg-background shadow-xs hover:bg-accent hover:text-accent-foreground hover:border-accent",
        ghost: "text-primary hover:bg-accent hover:text-accent-foreground",
        "ghost-clean": "text-accent hover:text-primary",
        link: "text-foreground underline-offset-4 hover:underline hover:text-accent",
        combobox:
          "font-light text rounded-md bg-white! border border-input bg-transparent px-3 py-1 text-base shadow-sm disabled:shadow-none disabled:text-black/30 transition-colors md:text-sm",
        carousel:
          "text-background [&>svg]:size-10! [&>svg]:stroke-1 hover:bg-background hover:text-foreground transition-colors duration-300 ease-in-out",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
